import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { MapPin, Clock, Users, Shield, ChevronRight, Check, CalendarDays, BarChart3, FileSpreadsheet, CheckCircle, Loader2 } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import { LanguageSelector } from "@/components/LanguageSelector";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PricingCard = ({ name, price, period, description, employees, features, cta, popular, onCta, isLoading }) => (
  <Card 
    className={`relative transition-all duration-300 ease-out transform hover:scale-105 hover:shadow-2xl hover:ring-2 hover:ring-primary hover:ring-offset-4 hover:z-10 ${popular ? 'border-primary shadow-lg scale-105 z-10' : 'border-border'}`}
  >
    {popular && <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary">Most Popular</Badge>}
    <CardHeader className="pb-4">
      <CardTitle className="font-heading text-xl">{name}</CardTitle>
      <CardDescription>{description}</CardDescription>
      <div className="mt-4">
        {isLoading ? (
          <Loader2 className="w-6 h-6 animate-spin" />
        ) : price !== "Contact Us" && price !== "تواصل معنا" ? (
          <>
            <span className="text-4xl font-bold">{price}</span>
            <span className="text-muted-foreground"> SAR{period}</span>
          </>
        ) : (
          <span className="text-2xl font-bold">{price}</span>
        )}
      </div>
      <p className="text-sm text-muted-foreground mt-2">{employees}</p>
    </CardHeader>
    <CardContent>
      <ul className="space-y-3 mb-6">
        {features.map((f, i) => (
          <li key={i} className="flex items-start gap-2 text-sm">
            <Check className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
            <span>{f}</span>
          </li>
        ))}
      </ul>
      <Button onClick={onCta} className={`w-full ${popular ? 'bg-primary' : ''}`} variant={popular ? 'default' : 'outline'}>
        {cta}
      </Button>
    </CardContent>
  </Card>
);

const FeatureCard = ({ Icon, title, description, badge, className }) => (
  <Card className={`bg-card border-border hover:border-primary/20 hover:shadow-card-hover transition-all duration-300 ${className || ''}`}>
    <CardContent className="p-6">
      <div className="flex items-start justify-between mb-4">
        <div className="w-12 h-12 rounded-2xl bg-accent flex items-center justify-center">
          <Icon className="w-6 h-6 text-primary" />
        </div>
        {badge && (
          <Badge variant="secondary" className="text-xs bg-primary/10 text-primary border-0">
            {badge}
          </Badge>
        )}
      </div>
      <h3 className="font-heading font-semibold text-lg text-foreground mb-2">{title}</h3>
      <p className="text-muted-foreground text-sm">{description}</p>
    </CardContent>
  </Card>
);

export const LandingPage = () => {
  const navigate = useNavigate();
  const { t, isRTL } = useLanguage();
  const [isChecking, setIsChecking] = useState(true);
  const [pricingPlans, setPricingPlans] = useState(null);
  const [pricingLoading, setPricingLoading] = useState(true);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await fetch(`${API_URL}/api/auth/me`, { credentials: 'include' });
        if (response.ok) {
          const user = await response.json();
          if (!user.role) navigate('/setup');
          else if (user.role === 'employer') navigate('/employer');
          else navigate('/employee');
        }
      } catch (error) { /* Not logged in */ }
      finally { setIsChecking(false); }
    };
    checkAuth();
    
    // Fetch pricing plans
    const fetchPricing = async () => {
      try {
        const res = await fetch(`${API_URL}/api/pricing`);
        if (res.ok) {
          const data = await res.json();
          setPricingPlans(data.plans);
        }
      } catch (e) { console.error("Failed to fetch pricing"); }
      finally { setPricingLoading(false); }
    };
    fetchPricing();
  }, [navigate]);

  const handleLogin = () => {
    navigate('/auth');
  };
  
  // Helper to get plan price for display
  const getPlanPrice = (planId) => {
    if (!pricingPlans || !pricingPlans[planId]) return null;
    return pricingPlans[planId].price;
  };
  
  const getPlanEmployees = (planId) => {
    if (!pricingPlans || !pricingPlans[planId]) return null;
    const max = pricingPlans[planId].max_employees;
    if (max >= 999999) return t("planEnterpriseTeam");
    return `${t("upTo")} ${max} ${t("employees")}`;
  };

  if (isChecking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-muted-foreground">{t("loading")}</div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen bg-background ${isRTL ? 'rtl' : 'ltr'}`}>
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-md border-b border-border">
        <div className="max-w-7xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center">
              <MapPin className="w-4 h-4 text-primary-foreground" />
            </div>
            <span className="font-heading font-bold text-xl text-foreground">{t("appName")}</span>
          </div>
          <div className="flex items-center gap-4">
            <a href="#features" className="hidden md:inline text-sm text-muted-foreground hover:text-foreground">{t("navFeatures")}</a>
            <a href="#pricing" className="hidden md:inline text-sm text-muted-foreground hover:text-foreground">{t("navPricing")}</a>
            <LanguageSelector />
            <Button onClick={handleLogin} data-testid="nav-login-btn" className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-6">
              {t("signIn")}
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 md:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="animate-fade-in">
              <span className="inline-block px-4 py-2 rounded-full bg-accent text-accent-foreground text-sm font-medium mb-6">
                {t("heroTagline")}
              </span>
              <h1 className="font-heading font-extrabold text-4xl sm:text-5xl lg:text-6xl text-foreground leading-tight mb-6">
                {t("heroTitle")} <span className="text-primary">{t("heroPrecision")}</span>
              </h1>
              <p className="text-lg text-muted-foreground mb-8 max-w-lg">
                {t("heroDescription")}
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button onClick={handleLogin} data-testid="hero-get-started-btn" size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full h-14 px-8 text-lg font-medium">
                  {t("startFreeTrial")}
                  <ChevronRight className="w-5 h-5 ml-2" />
                </Button>
              </div>
              <p className="text-sm text-muted-foreground mt-4">{t("heroTrust")}</p>
            </div>
            <div className="relative animate-slide-up stagger-2">
              <div className="aspect-[4/3] rounded-3xl overflow-hidden shadow-card-hover">
                <img src="https://images.unsplash.com/photo-1690383921891-3f0a7567d815?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwxfHxkaXZlcnNlJTIwc3RhcnR1cCUyMHRlYW0lMjB3b3JraW5nJTIwYnJpZ2h0JTIwb2ZmaWNlfGVufDB8fHx8MTc3MDIzNDUwOHww&ixlib=rb-4.1.0&q=85" alt="Team working" className="w-full h-full object-cover" />
              </div>
              <div className={`absolute -bottom-6 ${isRTL ? '-right-6' : '-left-6'} bg-card rounded-2xl p-4 shadow-card-hover border border-border`}>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="font-medium text-sm text-foreground">{t("heroVerified")}</p>
                    <p className="text-xs text-muted-foreground font-mono">37.7749° N, 122.4194° W</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 px-4 md:px-8 bg-muted/50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-foreground mb-4">{t("featuresTitle")}</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">{t("featuresSubtitle")}</p>
          </div>
          
          {/* Main Features - Reordered by employer priority */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            <FeatureCard 
              Icon={MapPin} 
              title={t("featureGPS")} 
              description={t("featureGPSDesc")} 
              badge={t("coreFeature")}
            />
            <FeatureCard 
              Icon={Clock} 
              title={t("featureTime")} 
              description={t("featureTimeDesc")} 
            />
            <FeatureCard 
              Icon={CalendarDays} 
              title={t("featureSchedule")} 
              description={t("featureScheduleDesc")}
              badge={t("mostUsed")}
            />
            <FeatureCard 
              Icon={FileSpreadsheet} 
              title={t("featurePayroll")} 
              description={t("featurePayrollDesc")} 
            />
            <FeatureCard 
              Icon={Users} 
              title={t("featureTeam")} 
              description={t("featureTeamDesc")} 
            />
            <FeatureCard 
              Icon={Shield} 
              title={t("featureSecurity")} 
              description={t("featureSecurityDesc")} 
            />
          </div>

          {/* Analytics Card - Full Width */}
          <Card className="bg-gradient-to-br from-primary/5 to-primary/10 border-primary/20">
            <CardContent className="p-8 flex flex-col md:flex-row items-center gap-6">
              <div className="w-16 h-16 rounded-2xl bg-primary/20 flex items-center justify-center flex-shrink-0">
                <BarChart3 className="w-8 h-8 text-primary" />
              </div>
              <div>
                <h3 className="font-heading font-semibold text-xl mb-2">{t("featureAnalytics")}</h3>
                <p className="text-muted-foreground">{t("featureAnalyticsDesc")}</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 px-4 md:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-foreground mb-4">{t("pricingTitle")}</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">{t("pricingSubtitle")}</p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-6">
            <PricingCard 
              name={t("planStarter")}
              price="0"
              period={` / ${t("trialDays")}`}
              description={t("planStarterDesc")}
              employees={t("planStarterTeam")}
              features={[t("allFeaturesIncluded"), t("planStarterFeature1"), t("planStarterFeature2")]}
              cta={t("startFreeTrial")}
              popular={false}
              onCta={handleLogin}
              isLoading={false}
            />
            <PricingCard 
              name={t("planGrowth")}
              price={getPlanPrice("growth") || "180"}
              period={`/${t("month")}`}
              description={t("planGrowthDesc")}
              employees={getPlanEmployees("growth") || t("planGrowthTeam")}
              features={[t("allFeaturesIncluded"), t("planGrowthFeature1"), t("planGrowthFeature2")]}
              cta={t("getStarted")}
              popular={false}
              onCta={handleLogin}
              isLoading={pricingLoading}
            />
            <PricingCard 
              name={t("planBusiness")}
              price={getPlanPrice("business") || "360"}
              period={`/${t("month")}`}
              description={t("planBusinessDesc")}
              employees={getPlanEmployees("business") || t("planBusinessTeam")}
              features={[t("allFeaturesIncluded"), t("planBusinessFeature1"), t("planBusinessFeature2"), t("planBusinessFeature3")]}
              cta={t("getStarted")}
              popular={true}
              onCta={handleLogin}
              isLoading={pricingLoading}
            />
            <PricingCard 
              name={t("planProfessional")}
              price={getPlanPrice("professional") || "500"}
              period={`/${t("month")}`}
              description={t("planProfessionalDesc")}
              employees={getPlanEmployees("professional") || t("planProfessionalTeam")}
              features={[t("allFeaturesIncluded"), t("planProfessionalFeature1"), t("planProfessionalFeature2"), t("planProfessionalFeature3")]}
              cta={t("getStarted")}
              popular={false}
              onCta={handleLogin}
              isLoading={pricingLoading}
            />
            <PricingCard 
              name={t("planEnterprise")}
              price={t("contactUs")}
              period=""
              description={t("planEnterpriseDesc")}
              employees={t("planEnterpriseTeam")}
              features={[t("allFeaturesIncluded"), t("planEnterpriseFeature1"), t("planEnterpriseFeature2"), t("planEnterpriseFeature3")]}
              cta={t("contactUs")}
              popular={false}
              onCta={() => navigate('/contact')}
              isLoading={false}
            />
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 md:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <div className="bg-primary rounded-3xl p-12 md:p-16">
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-primary-foreground mb-6">{t("ctaTitle")}</h2>
            <p className="text-primary-foreground/80 mb-8 max-w-lg mx-auto">{t("ctaDescription")}</p>
            <Button onClick={handleLogin} data-testid="cta-get-started-btn" size="lg" className="bg-white text-primary hover:bg-white/90 rounded-full h-14 px-8 text-lg font-medium">
              {t("startFreeTrial")}
              <ChevronRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 md:px-8 border-t border-border bg-muted/30">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center">
                  <MapPin className="w-4 h-4 text-primary-foreground" />
                </div>
                <span className="font-heading font-bold text-lg">{t("appName")}</span>
              </div>
              <p className="text-sm text-muted-foreground mb-3">{t("footerTagline")}</p>
              <p className="text-xs text-muted-foreground/70">{t("footerTrust")}</p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">{t("footerProduct")}</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li><a href="#features" className="hover:text-foreground">{t("navFeatures")}</a></li>
                <li><a href="#pricing" className="hover:text-foreground">{t("navPricing")}</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">{t("footerSupport")}</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li><a href="mailto:geocheck.inc@gmail.com" className="hover:text-foreground">{t("contactUs")}</a></li>
                <li className="text-xs">geocheck.inc@gmail.com</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">{t("footerLegal")}</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li><Link to="/terms" className="hover:text-foreground">{t("termsOfService")}</Link></li>
                <li><Link to="/privacy" className="hover:text-foreground">{t("privacyPolicy")}</Link></li>
              </ul>
            </div>
          </div>
          <div className="pt-8 border-t border-border flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-sm text-muted-foreground">© 2025 GeoCheck. {t("allRightsReserved")}.</p>
            <div className="flex items-center gap-4 text-sm text-muted-foreground">
              <Link to="/terms" className="hover:text-foreground">{t("termsOfService")}</Link>
              <Link to="/privacy" className="hover:text-foreground">{t("privacyPolicy")}</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
